import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Add-to-cart card used by the underlay upsell, the cart product upsell
 * and the cart cross-sale cards (which also carry a size select).
 * Adds `data-variant-id` to the cart; the resulting CartAddEvent morphs
 * both cart surfaces, which removes this card server-side (the Liquid
 * "already in cart" check).
 *
 * When `data-parent-key` is present (the underlay upsell) the add-on goes in as a nested
 * cart line under that parent. Cards without it — the cart product upsell and cross-sales —
 * add a plain top-level line, unchanged.
 *
 * @extends {Component}
 */
class ApcCartUpsell extends Component {
  requiredRefs = ['addButton'];

  /**
   * Syncs the card to the size chosen in the select (cross-sale cards):
   * updates the variant to add and shows the matching price.
   * Called by the declarative change handler.
   * @param {Event} event
   */
  selectVariant(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.value) return;
    this.dataset.variantId = select.value;
    for (const price of this.querySelectorAll('[data-variant-price]')) {
      if (price instanceof HTMLElement) price.hidden = price.dataset.variantPrice !== select.value;
    }
  }

  /**
   * Adds the upsell variant to cart. Called by the declarative click handler.
   */
  async addToCart() {
    const { addButton } = this.refs;
    const variantId = this.dataset.variantId;
    if (!variantId) {
      // No size picked yet — point the user at the select.
      if (this.refs.sizeSelect instanceof HTMLElement) this.refs.sizeSelect.focus();
      return;
    }
    if (!(addButton instanceof HTMLButtonElement) || addButton.disabled) return;
    addButton.disabled = true;

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    // `parent_line_key` makes this a nested cart line under its parent rug, which Shopify
    // honours through checkout: it orders the add-on directly beneath the rug and removes it
    // when the rug goes. It is the only way to nest under a line that already exists —
    // `parent_id` (a variant id) is rejected for existing lines and is what the PDP uses,
    // where rug and underlay are added in the same request.
    //
    // The key comes from the last server render and stays valid: unrelated cart mutations do
    // NOT rotate line keys (verified). A stale key returns a 422, handled by the catch below.
    const parentKey = this.dataset.parentKey;
    /** @type {{ id: number, quantity: number, parent_line_key?: string }} */
    const item = {
      id: Number(variantId),
      // One pad per rug, so the add-on mirrors the parent line's quantity. Absent on the
      // cross-sale cards, which add a single unit.
      quantity: Number(this.dataset.parentQuantity) || 1,
    };
    if (parentKey) item.parent_line_key = parentKey;

    const body = JSON.stringify({
      items: [item],
      sections: sections || undefined,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body }),
      });
      const result = await response.json();

      // Shopify flags add failures via a `status` field, even on a 200.
      if (!response.ok || result.status) {
        throw new Error(result.message || String(response.status));
      }

      // cart-icon treats itemCount from non-product-form sources as the absolute
      // cart total, so read the new total from the re-rendered section.
      let itemCount = 1;
      const sectionHtml = result.sections?.[sections.split(',')[0]];
      if (sectionHtml) {
        const sectionDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const newCount = Number(sectionDoc.querySelector('[ref="cartItemCount"]')?.textContent);
        if (newCount > 0) itemCount = newCount;
      }

      document.dispatchEvent(
        new CartAddEvent(result, 'apc-cart-upsell', {
          source: 'apc-cart-upsell',
          itemCount,
          variantId,
          sections: result.sections,
        }),
      );
      // Success: the section morph removes this card entirely.
    } catch (error) {
      console.error('[apc-cart-upsell] Failed to add underlay', error);
      addButton.disabled = false;
    }
  }
}

if (!customElements.get('apc-cart-upsell')) {
  customElements.define('apc-cart-upsell', ApcCartUpsell);
}
